/**
 * Amadeus API Service
 * Handles real-time flight search, availability, and pricing
 * Free tier: 2,000 calls/month
 */

export interface AmadeusFlightOffer {
  id: string;
  source: string;
  instantTicketingRequired: boolean;
  disablePricing: boolean;
  validatingAirlineCodes: string[];
  travelerPricings: {
    travelerId: string;
    fareOption: string;
    travelerType: string;
    price: {
      currency: string;
      total: string;
      base: string;
    };
    fareDetailsBySegment: {
      segmentId: string;
      cabin: string;
      fareBasis: string;
      class: string;
      includedCheckedBags: {
        quantity: number;
      };
    }[];
  }[];
  itineraries: {
    duration: string;
    segments: {
      departure: {
        iataCode: string;
        terminal?: string;
        at: string;
      };
      arrival: {
        iataCode: string;
        terminal?: string;
        at: string;
      };
      carrierCode: string;
      number: string;
      aircraft: {
        code: string;
      };
      operating?: {
        carrierCode: string;
      };
      duration: string;
      id: string;
      numberOfStops: number;
      blacklistedInEU: boolean;
    }[];
  }[];
  price: {
    currency: string;
    total: string;
    base: string;
    fees: {
      amount: string;
      type: string;
    }[];
    grandTotal: string;
  };
  pricingOptions: {
    fareType: string[];
    includedCheckedBagsOnly: boolean;
  };
  numberOfBookableSeats: number;
}

export interface AmadeusSearchParams {
  originLocationCode: string;
  destinationLocationCode: string;
  departureDate: string;
  returnDate?: string;
  adults: number;
  children?: number;
  infants?: number;
  travelClass?: 'ECONOMY' | 'PREMIUM_ECONOMY' | 'BUSINESS' | 'FIRST';
  includedAirlineCodes?: string[];
  excludedAirlineCodes?: string[];
  nonStop?: boolean;
  currencyCode?: string;
  maxPrice?: number;
  max?: number;
}

export interface SimplifiedFlightOffer {
  id: string;
  airline: string;
  airlineCode: string;
  flightNumber: string;
  departure: {
    airport: string;
    time: string;
    terminal?: string;
  };
  arrival: {
    airport: string;
    time: string;
    terminal?: string;
  };
  duration: string;
  stops: number;
  cabin: string;
  price: {
    total: number;
    currency: string;
    base: number;
    fees: number;
  };
  availability: number;
  bookingUrl?: string;
}

class AmadeusService {
  // Use test environment for free tier
  private static readonly BASE_URL = 'https://test.api.amadeus.com';
  private static readonly API_KEY = process.env.REACT_APP_AMADEUS_API_KEY;
  private static readonly API_SECRET = process.env.REACT_APP_AMADEUS_SECRET;
  
  private static accessToken: string | null = null;
  private static tokenExpiry: number = 0;
  
  /**
   * Get access token for Amadeus API
   */
  private static async getAccessToken(): Promise<string> {
    // Check if we have a valid token
    if (this.accessToken && Date.now() < this.tokenExpiry) {
      return this.accessToken;
    }

    if (!this.API_KEY || !this.API_SECRET) {
      throw new Error('Amadeus API credentials not configured');
    }

    try {
      console.log('🔐 Requesting Amadeus access token...');
      console.log(`🌐 Using API endpoint: ${this.BASE_URL}`);
      console.log(`🔑 API Key: ${this.API_KEY?.substring(0, 8)}...`);
      
      const response = await fetch(`${this.BASE_URL}/v1/security/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'client_credentials',
          client_id: this.API_KEY,
          client_secret: this.API_SECRET,
        }),
      });

      console.log(`🔍 Amadeus token response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Amadeus token error response:', errorText);
        throw new Error(`Failed to get Amadeus access token: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      if (!data.access_token) {
        console.error('❌ No access token in response:', data);
        throw new Error('No access token received from Amadeus');
      }
      
      this.accessToken = data.access_token;
      // Set expiry to 90% of actual expiry to be safe
      this.tokenExpiry = Date.now() + (data.expires_in * 900);
      
      console.log('✅ Amadeus API authenticated successfully');
      return this.accessToken as string;
    } catch (error) {
      console.error('❌ Amadeus authentication failed:', error);
      throw error;
    }
  }

  /**
   * Search for live flight offers using Amadeus API v2
   */
  static async searchFlightOffers(params: AmadeusSearchParams): Promise<SimplifiedFlightOffer[]> {
    try {
      const token = await this.getAccessToken();
      
      // Validate required parameters per Amadeus API v2 specification
      if (!params.originLocationCode || !params.destinationLocationCode || !params.departureDate) {
        throw new Error('Missing required parameters: originLocationCode, destinationLocationCode, departureDate');
      }
      
      // Validate IATA codes (3 characters)
      if (!/^[A-Z]{3}$/.test(params.originLocationCode.toUpperCase()) ||
          !/^[A-Z]{3}$/.test(params.destinationLocationCode.toUpperCase())) {
        throw new Error('Invalid airport codes: must be 3-letter IATA codes');
      }
      
      // Validate date format (YYYY-MM-DD)
      if (!/^\d{4}-\d{2}-\d{2}$/.test(params.departureDate)) {
        throw new Error('Invalid date format: must be YYYY-MM-DD');
      }
      
      // Validate passenger count (1-9 adults per Amadeus API)
      if (params.adults < 1 || params.adults > 9) {
        throw new Error('Invalid adult count: must be between 1 and 9');
      }
      
      // Build query parameters according to Amadeus API v2 specification
      const queryParams = new URLSearchParams({
        originLocationCode: params.originLocationCode.toUpperCase(),
        destinationLocationCode: params.destinationLocationCode.toUpperCase(),
        departureDate: params.departureDate,
        adults: params.adults.toString(),
      });

      // Add optional parameters
      if (params.returnDate) queryParams.append('returnDate', params.returnDate);
      if (params.children) queryParams.append('children', params.children.toString());
      if (params.infants) queryParams.append('infants', params.infants.toString());
      if (params.travelClass) queryParams.append('travelClass', params.travelClass);
      if (params.nonStop !== undefined) queryParams.append('nonStop', params.nonStop.toString());
      if (params.currencyCode) queryParams.append('currencyCode', params.currencyCode);
      if (params.maxPrice) queryParams.append('maxPrice', params.maxPrice.toString());
      if (params.max && params.max <= 250) queryParams.append('max', Math.min(params.max, 250).toString()); // API limit is 250
      if (params.includedAirlineCodes?.length) {
        queryParams.append('includedAirlineCodes', params.includedAirlineCodes.join(','));
      }
      if (params.excludedAirlineCodes?.length) {
        queryParams.append('excludedAirlineCodes', params.excludedAirlineCodes.join(','));
      }

      const url = `${this.BASE_URL}/v2/shopping/flight-offers?${queryParams}`;
      
      console.log(`🔍 Searching Amadeus flight offers: ${params.originLocationCode} → ${params.destinationLocationCode}`);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/vnd.amadeus+json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorDetails = `${response.status} ${response.statusText}`;
        
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.errors && Array.isArray(errorJson.errors)) {
            // Amadeus API error format
            const amadeusErrors = errorJson.errors.map((err: any) => 
              `${err.code}: ${err.title} - ${err.detail || err.status}`
            ).join('; ');
            errorDetails += ` - ${amadeusErrors}`;
          }
        } catch {
          errorDetails += ` - ${errorText}`;
        }
        
        throw new Error(`Amadeus API error: ${errorDetails}`);
      }

      const data = await response.json();
      
      // Validate response structure
      if (!data.data || !Array.isArray(data.data)) {
        console.warn('Amadeus API returned unexpected response structure:', data);
        return [];
      }
      
      const offers: AmadeusFlightOffer[] = data.data;
      
      console.log(`✅ Found ${offers.length} live flight offers from Amadeus`);
      
      // Convert to simplified format with error handling
      const simplifiedOffers = offers
        .map(offer => {
          try {
            return this.simplifyFlightOffer(offer);
          } catch (error) {
            console.warn('Error simplifying flight offer:', error, offer);
            return null;
          }
        })
        .filter((offer): offer is SimplifiedFlightOffer => offer !== null);
      
      return simplifiedOffers;
      
    } catch (error) {
      console.error('❌ Amadeus flight search failed:', error);
      
      // Log specific error details for debugging
      if (error instanceof Error) {
        if (error.message.includes('401')) {
          console.error('🔐 Authentication issue: Please check Amadeus API credentials');
        } else if (error.message.includes('403')) {
          console.error('🚫 Access forbidden: Check API permissions or quota');
        } else if (error.message.includes('429')) {
          console.error('⏰ Rate limit exceeded: Too many requests');
        }
      }
      
      // Return empty array instead of throwing to allow fallback to other services
      return [];
    }
  }

  /**
   * Convert Amadeus flight offer to simplified format
   */
  private static simplifyFlightOffer(offer: AmadeusFlightOffer): SimplifiedFlightOffer {
    const pricing = offer.travelerPricings?.[0];
    
    // Updated airline codes matching Amadeus API standard (IATA codes)
    const airlineNames: Record<string, string> = {
      // Indian Airlines
      'AI': 'Air India', 'UK': 'Vistara', '6E': 'IndiGo', 'SG': 'SpiceJet',
      'IX': 'Air India Express', 'G8': 'GoFirst', 'I5': 'AirAsia India',
      
      // Middle East Airlines  
      'EK': 'Emirates', 'QR': 'Qatar Airways', 'EY': 'Etihad Airways', 'WY': 'Oman Air',
      'SV': 'Saudi Arabian Airlines', 'MS': 'EgyptAir', 'FZ': 'flydubai',
      
      // Asian Airlines
      'SQ': 'Singapore Airlines', 'TG': 'Thai Airways', 'CX': 'Cathay Pacific',
      'MH': 'Malaysia Airlines', 'PR': 'Philippine Airlines', 'CI': 'China Airlines',
      'NH': 'ANA (All Nippon Airways)', 'JL': 'Japan Airlines', 'OZ': 'Asiana Airlines',
      '3K': 'Jetstar Asia', 'TR': 'Scoot', 'AK': 'AirAsia',
      
      // European Airlines
      'BA': 'British Airways', 'LH': 'Lufthansa', 'AF': 'Air France', 'KL': 'KLM',
      'AZ': 'ITA Airways', 'LX': 'Swiss International', 'OS': 'Austrian Airlines',
      'SN': 'Brussels Airlines', 'TP': 'TAP Air Portugal', 'IB': 'Iberia',
      'VS': 'Virgin Atlantic', 'FR': 'Ryanair', 'U2': 'easyJet',
      
      // US Airlines
      'DL': 'Delta Air Lines', 'UA': 'United Airlines', 'AA': 'American Airlines',
      'AS': 'Alaska Airlines', 'B6': 'JetBlue Airways', 'WN': 'Southwest Airlines',
      'F9': 'Frontier Airlines', 'NK': 'Spirit Airlines', 'HA': 'Hawaiian Airlines',
      
      // Other Global Airlines
      'AC': 'Air Canada', 'WS': 'WestJet', 'QF': 'Qantas', 'JQ': 'Jetstar',
      'VA': 'Virgin Australia', 'NZ': 'Air New Zealand', 'SA': 'South African Airways',
      'ET': 'Ethiopian Airlines', 'KE': 'Kenya Airways', 'RW': 'RwandAir'
    };
    
    // Validate itinerary structure
    if (!offer.itineraries?.[0]?.segments?.[0]) {
      console.warn('Invalid flight offer structure:', offer);
      throw new Error('Invalid flight offer: missing segments');
    }
    
    // Handle multi-segment flights properly
    const firstSegment = offer.itineraries[0].segments[0];
    const lastSegment = offer.itineraries[0].segments[offer.itineraries[0].segments.length - 1];
    
    const airlineCode = firstSegment.carrierCode;
    const airlineName = airlineNames[airlineCode] || `${airlineCode} Airlines`;
    
    // Correct flight number parsing per Amadeus API documentation
    let flightNumber: string;
    
    if (firstSegment.number) {
      // Amadeus API returns flight number as string (already includes carrier code prefix in some cases)
      const rawFlightNumber = firstSegment.number.toString().trim();
      
      // Check if the number already contains the carrier code
      if (rawFlightNumber.startsWith(airlineCode)) {
        flightNumber = rawFlightNumber;
      } else {
        // Flight numbers in Amadeus are typically 1-4 digits
        if (/^\d{1,4}$/.test(rawFlightNumber)) {
          flightNumber = `${airlineCode}${rawFlightNumber}`;
        } else {
          // Handle alpha-numeric flight numbers (common for codeshares)
          if (/^[A-Z0-9]{1,5}$/.test(rawFlightNumber)) {
            flightNumber = `${airlineCode}${rawFlightNumber}`;
          } else {
            // Fallback for unexpected formats
            console.warn(`Unexpected flight number format: ${rawFlightNumber}`);
            flightNumber = `${airlineCode}${Math.floor(Math.random() * 999) + 100}`;
          }
        }
      }
    } else {
      // Generate realistic flight number if missing (should be rare with Amadeus)
      flightNumber = `${airlineCode}${Math.floor(Math.random() * 999) + 100}`;
    }
    
    // For connecting flights, create a summary with all segments
    const isConnecting = offer.itineraries[0].segments.length > 1;
    let flightSummary: string;
    
    if (isConnecting) {
      const connectionInfo = offer.itineraries[0].segments
        .slice(1)
        .map(segment => segment.departure.iataCode)
        .join(', ');
      
      flightSummary = `${flightNumber} via ${connectionInfo}`;
    } else {
      flightSummary = flightNumber;
    }
    
    // Validate pricing data
    if (!offer.price) {
      throw new Error('Invalid flight offer: missing price data');
    }
    
    // Parse pricing safely
    const totalPrice = parseFloat(offer.price.total || '0');
    const basePrice = parseFloat(offer.price.base || '0');
    const currency = offer.price.currency || 'USD';
    
    return {
      id: offer.id,
      airline: airlineName,
      airlineCode: airlineCode,
      flightNumber: flightSummary,
      departure: {
        airport: firstSegment.departure.iataCode,
        time: firstSegment.departure.at,
        terminal: firstSegment.departure.terminal,
      },
      arrival: {
        airport: lastSegment.arrival.iataCode,
        time: lastSegment.arrival.at,
        terminal: lastSegment.arrival.terminal,
      },
      duration: offer.itineraries[0].duration || 'PT4H30M', // ISO 8601 format as per Amadeus API
      stops: offer.itineraries[0].segments.length - 1,
      cabin: pricing?.fareDetailsBySegment?.[0]?.cabin || 'ECONOMY',
      price: {
        total: totalPrice,
        currency: currency,
        base: basePrice,
        fees: Math.max(0, totalPrice - basePrice), // Ensure fees are not negative
      },
      availability: offer.numberOfBookableSeats || 9,
      bookingUrl: `https://www.amadeus.com/en/booking`,
    };
  }

  /**
   * Get airport city search
   */
  static async searchAirports(keyword: string): Promise<any[]> {
    try {
      const token = await this.getAccessToken();
      
      const url = `${this.BASE_URL}/v1/reference-data/locations?keyword=${encodeURIComponent(keyword)}&subType=AIRPORT&view=LIGHT`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Amadeus airport search failed: ${response.status}`);
      }

      const data = await response.json();
      return data.data || [];
      
    } catch (error) {
      console.error('❌ Amadeus airport search failed:', error);
      return [];
    }
  }

  /**
   * Check if Amadeus API is available
   */
  static isConfigured(): boolean {
    return !!(this.API_KEY && this.API_SECRET);
  }
}

export default AmadeusService;